<template>
  <div class="select-container">
    <n-input
      class="select-input"
      v-bind="inputProps"
      :size="size"
      :value="displayValue"
      autocomplete="off"
      @click="toggleDropdown"
      @focus="onFocus"
      @blur="onBlur"
      @input="onSearchInput"
      @keydown="onKeydown"
      @clickiconRight="onClickRightIcon"
      @imageLeftError="onImageError(imageLeft)"
      :iconRight="rightIcon"
      ref="input"
    />
    <n-menu class="dropdown" ref="dropdown" :tippyProps="menuProps">
      <n-list-item
        v-for="(entry, index) in optionEntries"
        :key="index"
        :active="activeElement === index"
        @mousedown.prevent="onSelect(index)"
        @mouseenter="activeElement = index"
      >
        <span v-if="entry.image" class="option-content">
          <img
            class="option-image"
            :src="entry.image"
            alt=""
            width="16"
            height="16"
            loading="lazy"
            decoding="async"
            @error="onImageError(entry.image)"
          />
          <span class="option-label">{{ entry.label }}</span>
        </span>
        <template v-else>{{ entry.label }}</template>
      </n-list-item>
    </n-menu>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import NInput, { type InputSize } from './NInput.vue'
import NMenu from '@/modules/global/components/core/NMenu/NMenu.vue'
import { Instance } from 'tippy.js'

@Component({
  components: { NInput },
  model: {
    prop: 'value',
    event: 'select'
  }
})
export default class NSelect<T> extends Vue {
  @Prop({ default: undefined }) value?: T
  @Prop({ default: undefined }) label?: NInput['label']
  @Prop({ default: '' }) placeholder!: HTMLInputElement['placeholder']
  @Prop({ default: 'm' }) size!: InputSize

  @Prop({ default: () => [] }) options!: T[] | Record<string, T>
  @Prop({ default: (opt: T) => opt, type: Function }) toLabel!: (option: T) => string
  @Prop({ default: undefined, type: Function }) toImageLeft?: (option: T) => string | undefined

  @Prop({ default: undefined, type: Boolean }) disabled?: boolean
  @Prop({ default: false, type: Boolean }) clearable!: boolean

  @Ref('input') inputRef!: NInput
  @Ref('dropdown') dropdownRef!: NMenu

  private activeElement = -1
  private searchQuery = ''
  private isSearching = false
  private failedImages: string[] = []

  private get inputProps() {
    return {
      label: this.label,
      disabled: this.disabled,
      placeholder: this.placeholder,
      imageLeft: this.imageLeft
    }
  }

  private get imageLeft(): string | undefined {
    if (this.isSearching || this.value == null) return undefined
    return this.resolveImage(this.value)
  }

  get optionEntries(): { label: string; image?: string }[] {
    return this.filteredOptions.map(option => ({
      label: this.toLabel(option),
      image: this.resolveImage(option)
    }))
  }

  /** Drops images that failed to load (e.g. the CDN is blocked) so no broken icon is shown. */
  private resolveImage(option: T): string | undefined {
    const image = this.toImageLeft?.(option)
    return image && !this.failedImages.includes(image) ? image : undefined
  }

  onImageError(image?: string) {
    if (image && !this.failedImages.includes(image)) {
      this.failedImages.push(image)
    }
  }

  get optionList(): T[] {
    return Array.isArray(this.options) ? this.options : Object.values(this.options)
  }

  get filteredOptions(): T[] {
    const query = this.searchQuery.trim().toLowerCase()
    if (!query) return this.optionList
    return this.optionList.filter(option => this.toLabel(option).toLowerCase().includes(query))
  }

  get displayValue(): string | undefined {
    if (this.isSearching) return this.searchQuery
    if (this.value == null) return undefined
    return this.toLabel(this.value) || undefined
  }

  get hasValue(): boolean {
    return this.value != null && `${this.value}` !== ''
  }

  get showClearIcon(): boolean {
    return this.clearable && this.hasValue && !this.disabled
  }

  get rightIcon(): string {
    return this.showClearIcon ? 'x' : 'chevron-down'
  }

  get menuProps() {
    return {
      hideOnClick: true,
      maxWidth: '100%',
      zIndex: 1500,
      onCreate: (instance: Instance) => {
        const popperElement = instance.popper as HTMLDivElement
        popperElement.querySelector<HTMLDivElement>(
          '.menu-wrapper'
        )!.style.width = `${this.inputRef?.$el.clientWidth}px`
      }
    }
  }

  onFocus() {
    this.$nextTick(() => {
      const input = this.inputRef.$refs.input as HTMLInputElement | undefined
      input?.select()
    })
  }

  onBlur() {
    this.resetSearch()
  }

  onSearchInput(query: string) {
    this.isSearching = true
    this.searchQuery = query
    this.activeElement = this.filteredOptions.length ? 0 : -1
    this.openMenu()
  }

  onKeydown(event: KeyboardEvent) {
    if (this.disabled) return

    switch (event.key) {
      case 'Escape':
        this.closeMenu()
        this.resetSearch()
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!this.dropdownRef.isOpen()) {
          this.openMenu()
          this.activeElement = 0
        } else {
          this.activeElement = Math.min(this.activeElement + 1, Math.max(this.filteredOptions.length - 1, 0))
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (!this.dropdownRef.isOpen()) {
          this.openMenu()
          this.activeElement = Math.max(this.filteredOptions.length - 1, 0)
        } else {
          this.activeElement = Math.max(this.activeElement - 1, 0)
        }
        break
      case 'Enter':
        event.preventDefault()
        if (this.dropdownRef.isOpen() && this.activeElement >= 0) {
          this.onSelect(this.activeElement)
        } else {
          this.openMenu()
        }
        break
      case ' ':
        if (!this.dropdownRef.isOpen() && !this.isSearching) {
          event.preventDefault()
          this.openMenu()
        }
        break
    }
  }

  onSelect(index: number) {
    const option = this.filteredOptions[index]
    if (option === undefined) return
    this.resetSearch()
    this.$emit('select', option)
    this.closeMenu()
  }

  onClickRightIcon(event?: MouseEvent) {
    if (!this.showClearIcon) return
    event?.stopPropagation()
    this.clear()
  }

  clear() {
    this.resetSearch()
    this.closeMenu()
    this.$emit('select', undefined)
  }

  resetSearch() {
    this.isSearching = false
    this.searchQuery = ''
  }

  openMenu() {
    if (this.disabled || this.dropdownRef.isOpen()) return
    this.dropdownRef.open(this.inputRef.$el as HTMLElement)
    if (this.activeElement < 0 && this.filteredOptions.length) {
      this.activeElement = 0
    }
  }

  closeMenu() {
    this.dropdownRef.close()
    this.activeElement = -1
  }

  toggleDropdown() {
    if (this.disabled) return
    if (this.dropdownRef.isOpen()) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.select-container {
  position: relative;
  box-sizing: border-box;
  cursor: pointer;
}

.select-container:focus {
  outline: none;
}

.select-input {
  position: relative;
  z-index: $notch-layer-1;
}

.option-content {
  display: flex;
  align-items: center;
  gap: $notch-space-2x;
  min-width: 0;
}

.option-image {
  flex: none;
  width: $notch-space-4x;
  height: $notch-space-4x;
  object-fit: contain;
}

.option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown {
  max-height: $notch-space-100x;
  overflow: auto;
}
</style>
